import { addDays } from 'date-fns';
import {
  fieldInstallationPreferredArrivalWindowVulcan,
  fieldCommercialPremise,
  fieldStoreCodeVulcan,
  fieldStoreCodeOutletVulcan,
  fieldRcidVulcan,
  fieldBanVulcan,
  fieldStreetNameVul,
  fieldStreetNumberVul,
  fieldCityVul,
  fieldProvinceVUL,
  fieldPostalCodeVul,
  fieldWirelinePreferedInstallDateVulcan,
  fieldSecondaryInstallDateVulcan,
  valueFibreInternet5GSpeed,
  getInstallDateBufferDays,
  CONSTANT
} from '../../../utils/constant';
import {
  fibreSpeed,
  offers,
  fieldInstallation,
  fieldServiceAddressNoteFibreVul
} from '../../vulcanPricing/fibeInternet/fibreInternetFieldNames';

const { CORP_STORE, DEALER } = CONSTANT.KEYS.CHANNELS;

// Faithful port of the inline `isServiceAddressFilled` guard — returns true when
// ANY required service-address field on orderData is empty (i.e. NOT fully filled).
function serviceAddressIncomplete(orderData) {
  return [
    fieldStreetNameVul,
    fieldStreetNumberVul,
    fieldCityVul,
    fieldProvinceVUL,
    fieldPostalCodeVul
  ].some(f => {
    const value = orderData[f];
    return value === undefined || value.trim() === '';
  });
}

/**
 * Pure rewrite of the self-contained per-field conditionals inlined in
 * `formStepVulcan.js`'s render loop (the block before the terminal field render):
 * channel/order-driven hides, required toggles, value resets, and install-date
 * minDates. Each `return null` (hide) → `{ visibility: false }`; each
 * `formFields[x].value = …` / `isRequired = …` → the matching self-patch.
 *
 * Deferred (need equipment-section nested state / pricing metadata, ported with
 * Stage 2c): the `secureBusinessPlan` / `secureBusinessOffers` `monthlyPayment`
 * option derivations. (The smoke/temperature-sensor note suppression that used
 * to be deferred here now lives in rules/secureBusiness.js.)
 *
 * @param {{orderData: Object, channelName?: string, t: Object}} ctx
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makeInlineFieldRules(ctx) {
  const { orderData, channelName, t } = ctx;

  return field => {
    const { name } = field;

    // Commercial premise: clear the preferred arrival window value.
    if (
      name === fieldInstallationPreferredArrivalWindowVulcan &&
      orderData[fieldCommercialPremise] === t.commercial &&
      orderData[fieldInstallationPreferredArrivalWindowVulcan]
    ) {
      return { value: '' };
    }

    // 5G selection: required only when the order's saved Fibre Internet
    // speed selection includes the 5G speed value. Replaces a prior
    // unconditional "hidden for a Vulcan order" hide (SOF-3442) — Contentful's
    // own visibility rule for this field now governs whether it renders at
    // all; this only toggles required-ness once it does.
    if (name === 'fivegSelected') {
      const speeds =
        (orderData.fibreInternetData && orderData.fibreInternetData.speed) ||
        [];
      const is5G =
        Array.isArray(speeds) && speeds.includes(valueFibreInternet5GSpeed);
      return { isRequired: is5G };
    }

    // Service address is hidden once the order already carries a province.
    if (
      (name === 'Service Address ' || name === 'Adresse de service') &&
      orderData.province_VUL
    ) {
      return { visibility: false };
    }

    // Store code + outlet: hidden and not required outside Corp Store.
    if (
      channelName !== CORP_STORE &&
      (name === fieldStoreCodeVulcan || name === fieldStoreCodeOutletVulcan)
    ) {
      return { visibility: false, isRequired: false };
    }

    if (channelName === CORP_STORE || channelName === DEALER) {
      // RCID hidden + not required for Corp Store / Dealer.
      if (name === fieldRcidVulcan) {
        return { visibility: false, isRequired: false };
      }
      // BAN stays visible but is not required.
      if (name === fieldBanVulcan) {
        return { isRequired: false, required: false };
      }
    }

    // Fibre speed / offers / installation hidden until the service address is
    // complete; speed & offers also clear their value.
    if (
      serviceAddressIncomplete(orderData) &&
      (name === fibreSpeed || name === offers || name === fieldInstallation)
    ) {
      const patch = { visibility: false, isRequired: false };
      if (name === fibreSpeed || name === offers) patch.value = undefined;
      return patch;
    }

    // The fibre service-address note shows only until the address is complete.
    if (
      !serviceAddressIncomplete(orderData) &&
      name === fieldServiceAddressNoteFibreVul
    ) {
      return { visibility: false };
    }

    // Install dates: earliest selectable date is a province-dependent buffer
    // out (SOF-3400/032-install-date-buffer-region) — 7 days for Western
    // Canada (BC/AB/SK/MB), 10 days everywhere else.
    if (
      name === fieldWirelinePreferedInstallDateVulcan ||
      name === fieldSecondaryInstallDateVulcan
    ) {
      const bufferDays = getInstallDateBufferDays(orderData[fieldProvinceVUL]);
      return { minDate: addDays(new Date(), bufferDays) };
    }

    return undefined;
  };
}

export default makeInlineFieldRules;
